/**
 * Moments as Home and People say them, in the workspace's zone: "4 min ago", "Tue 3 Nov", "Tuesday 3 November",
 * "6:00 PM". Times on the server are unix seconds; a session's start is an ISO string.
 */
import { appZone, dayNumber } from "./zones";

type At = number | string;

function ms(at: At): number {
  return typeof at === "string" ? Date.parse(at) : at * 1000;
}

function parts(at: At, opts: Intl.DateTimeFormatOptions): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat("en-US", { ...opts, timeZone: appZone() }).formatToParts(ms(at))) out[p.type] = p.value;
  return out;
}

/** "6:00 PM". */
export function clock(at: At): string {
  const p = parts(at, { hour: "numeric", minute: "2-digit" });
  return `${p.hour}:${p.minute} ${p.dayPeriod}`;
}

/** "Tue 3 Nov". */
export function shortDay(at: At): string {
  const p = parts(at, { weekday: "short", day: "numeric", month: "short" });
  return `${p.weekday} ${p.day} ${p.month}`;
}

/** "Tuesday 3 November". */
export function longDay(at: At): string {
  const p = parts(at, { weekday: "long", day: "numeric", month: "long" });
  return `${p.weekday} ${p.day} ${p.month}`;
}

/** "3 Nov". */
export function dayMonth(at: At): string {
  const p = parts(at, { day: "numeric", month: "short" });
  return `${p.day} ${p.month}`;
}

/** How many calendar days ago, in the workspace's zone: 0 today, 1 yesterday. */
function daysAgo(at: At, now = Date.now()): number {
  return dayNumber(now) - dayNumber(ms(at));
}

/** "Today", "Yesterday", or "Wednesday 28 October": a heading for one day of a story. */
export function dayHeading(at: At, now = Date.now()): string {
  const d = daysAgo(at, now);
  if (d === 0) return "Today";
  if (d === 1) return "Yesterday";
  return longDay(at);
}

/** "just now", "14 min ago", "2 hours ago", then "Yesterday", then "Tue 3 Nov". */
export function ago(at: At, now = Date.now()): string {
  const sec = Math.max(0, Math.floor((now - ms(at)) / 1000));
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24 && daysAgo(at, now) === 0) return h === 1 ? "1 hour ago" : `${h} hours ago`;
  if (daysAgo(at, now) === 1) return "Yesterday";
  return shortDay(at);
}

/** "tomorrow", "today", "in 3 days", or "Tue 3 Nov": when a session is, said from now. */
export function untilDay(at: At, now = Date.now()): string {
  const d = -daysAgo(at, now);
  if (d === 0) return "today";
  if (d === 1) return "tomorrow";
  if (d > 1 && d < 7) return `in ${d} days`;
  return shortDay(at);
}

export const fmt = (n: number): string => n.toLocaleString("en-US");
export const firstName = (name: string): string => name.trim().split(/\s+/)[0] ?? name;
